import { currency, formatMonth } from "./date.js";

function readableLabel(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function sheetSafeValue(value) {
  if (value == null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return value;
}

function addRecordsSheet(workbook, name, rows) {
  const sheet = workbook.addWorksheet(name.slice(0, 31));
  const records = Array.isArray(rows) ? rows : [];
  const keys = [...new Set(records.flatMap((row) => Object.keys(row || {})))];
  if (!keys.length) {
    sheet.addRow(["No records"]);
    return;
  }
  sheet.columns = keys.map((key) => ({ header: readableLabel(key), key, width: Math.min(34, Math.max(14, readableLabel(key).length + 4)) }));
  records.forEach((row) => {
    sheet.addRow(Object.fromEntries(keys.map((key) => [key, sheetSafeValue(row?.[key])])));
  });
  sheet.views = [{ state: "frozen", ySplit: 1 }];
  sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  sheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F4D3A" } };
}

export async function exportFullBackupExcel({ state, companyName }) {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "SparkingCircle";
  workbook.created = new Date();

  const backup = {
    backupVersion: 1,
    app: "SparkingCircle",
    companyName,
    exportedAt: new Date().toISOString(),
    data: state
  };

  const readme = workbook.addWorksheet("Read Me");
  readme.columns = [
    { header: "Item", key: "item", width: 28 },
    { header: "Value", key: "value", width: 80 }
  ];
  readme.addRows([
    { item: "Company", value: companyName || "Company" },
    { item: "Backup date", value: new Date().toLocaleString("en-IN") },
    { item: "What this contains", value: "Employees, users, stores, attendance, tasks, payroll, settings, approvals, audit logs and all other app data." },
    { item: "Restore data", value: "The Full Restore JSON sheet contains one complete machine-readable backup split into numbered chunks. Join chunks in Part order to rebuild the app data." },
    { item: "Developer restore note", value: "A developer can parse the JSON, read backup.data, and import each array back into the matching Supabase table after checking IDs and privacy rules." },
    { item: "Important", value: "Keep this file private. It can contain salary, phone, attendance and payroll data." }
  ]);
  readme.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  readme.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F4D3A" } };

  const jsonSheet = workbook.addWorksheet("Full Restore JSON");
  jsonSheet.columns = [
    { header: "Part", key: "part", width: 12 },
    { header: "JSON chunk", key: "chunk", width: 100 }
  ];
  const json = JSON.stringify(backup);
  const chunkSize = 30000;
  for (let index = 0; index < json.length; index += chunkSize) {
    jsonSheet.addRow({ part: Math.floor(index / chunkSize) + 1, chunk: json.slice(index, index + chunkSize) });
  }
  jsonSheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  jsonSheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F4D3A" } };

  addRecordsSheet(workbook, "Settings", Object.entries(state.settings || {}).map(([key, value]) => ({ key, value: sheetSafeValue(value) })));
  [
    ["Stores", state.stores],
    ["Employees", state.employees],
    ["Users", state.users],
    ["Attendance Punches", state.punches],
    ["Corrections", state.corrections],
    ["Tasks", state.workTasks],
    ["Sales Targets", state.salesTargets],
    ["Optical Customers", state.opticalCustomers],
    ["Optical Orders", state.opticalFastSlips],
    ["CRM Products", state.crmProducts],
    ["CRM Stock Movements", state.crmStockMovements],
    ["CRM Purchases", state.crmPurchases],
    ["CRM Communications", state.crmCommunications],
    ["CRM Prescriptions", state.crmPrescriptions],
    ["CRM Appointments", state.crmAppointments],
    ["CN Requests", state.cnRequests],
    ["CN Import Batches", state.importBatches],
    ["Notifications", state.notifications],
    ["Company Ledger Entries", state.companyLedgerEntries],
    ["App Files", state.appFiles],
    ["Bank Reco", state.bankReco],
    ["Payroll Runs", state.payrollRuns],
    ["Advances Loans", state.advancesLoans],
    ["Reimbursements", state.reimbursements],
    ["Collections", state.storeCollections],
    ["Incentive Rules", state.incentiveRuleHistory],
    ["Salary History", state.salaryHistory],
    ["Store History", state.storeHistory],
    ["Weekly Off History", state.weeklyOffHistory],
    ["Shift History", state.shiftHistory],
    ["Geofence History", state.geofenceHistory],
    ["Leave Balances", state.leaveBalances],
    ["Salary Slips", state.salarySlips],
    ["Approvals", state.approvals],
    ["Audit Logs", state.auditLogs]
  ].forEach(([name, rows]) => addRecordsSheet(workbook, name, rows || []));

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
}

export async function exportPayrollExcel({ payrollRun, employees, stores, companyName }) {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Salary Sheet", {
    views: [{ state: "frozen", ySplit: 4 }]
  });

  sheet.properties.defaultRowHeight = 20;
  sheet.columns = [
    { header: "Particulars", key: "label", width: 28 },
    ...payrollRun.items.map((item) => ({ header: item.employeeName, key: item.employeeId, width: 18 })),
    { header: "Totals", key: "totals", width: 18 }
  ];

  sheet.mergeCells(1, 1, 1, sheet.columnCount);
  sheet.getCell("A1").value = companyName || "Company";
  sheet.getCell("A1").font = { name: "Arial", size: 14, bold: true, color: { argb: "FFFFFFFF" } };
  sheet.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F4D3A" } };
  sheet.getCell("A2").value = `Payroll month: ${formatMonth(payrollRun.month)}`;
  sheet.getCell("A3").value = `Prepared: ${new Date().toLocaleDateString("en-IN")}`;

  const employeeMap = new Map(employees.map((employee) => [employee.id, employee]));
  const storeMap = new Map(stores.map((store) => [store.id, store]));

  const addPayrollRow = (label, getValue, totalValue = "") => {
    const row = { label };
    for (const item of payrollRun.items) row[item.employeeId] = getValue(item);
    row.totals = totalValue;
    sheet.addRow(row);
  };

  addPayrollRow("Store", (item) => storeMap.get(employeeMap.get(item.employeeId)?.storeId)?.name || "");
  addPayrollRow("Weekly off day", (item) => employeeMap.get(item.employeeId)?.weeklyOffDay || "");
  addPayrollRow("Monthly salary", (item) => currency(employeeMap.get(item.employeeId)?.monthlySalary || 0));
  addPayrollRow("Entitled weekly offs", (item) => item.attendanceSummary.entitledWeeklyOffs);
  addPayrollRow("Brought forward leave", (item) => item.attendanceSummary.broughtForwardLeave);
  addPayrollRow("Monthly half day", (item) => item.attendanceSummary.monthlyHalfDay);
  addPayrollRow("Total offs taken", (item) => item.attendanceSummary.totalOffsTaken);
  addPayrollRow("Carry forward", (item) => item.attendanceSummary.carryForward);
  addPayrollRow("Leave deduction days", (item) => item.attendanceSummary.leaveDeductionDays);
  addPayrollRow(
    "Leave deduction amount",
    (item) => item.attendanceSummary.leaveDeductionAmount,
    payrollRun.totals.leaveDeductionAmount
  );
  addPayrollRow("Advance / loan deduction", (item) => item.advanceDeduction, payrollRun.totals.advanceDeduction);
  addPayrollRow("Opening loan balance", (item) => item.openingLoanBalance);
  addPayrollRow("Closing loan balance", (item) => item.closingLoanBalance);
  addPayrollRow("Other deduction", (item) => item.otherDeduction);
  addPayrollRow("Staff welfare", (item) => item.staffWelfare, payrollRun.totals.staffWelfare);
  addPayrollRow("Reimbursements", (item) => item.reimbursementAmount, payrollRun.totals.reimbursements);
  addPayrollRow("Incentive payable", (item) => item.incentivePayable, payrollRun.totals.incentives);
  addPayrollRow("Net payable", (item) => item.netPayable, payrollRun.totals.netPayable);
  addPayrollRow("Monthly incentive record", () => "");
  addPayrollRow("Quarterly incentive record", () => "");

  sheet.eachRow((row, rowNumber) => {
    row.eachCell((cell) => {
      cell.font = { name: "Arial", size: 10, ...(cell.font || {}) };
      cell.border = {
        top: { style: "thin", color: { argb: "FFE2E8E0" } },
        left: { style: "thin", color: { argb: "FFE2E8E0" } },
        bottom: { style: "thin", color: { argb: "FFE2E8E0" } },
        right: { style: "thin", color: { argb: "FFE2E8E0" } }
      };
      if (rowNumber === 4) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F4D3A" } };
        cell.font = { name: "Arial", size: 10, bold: true, color: { argb: "FFFFFFFF" } };
      }
      if (cell.value === "Net payable") {
        row.font = { name: "Arial", size: 11, bold: true };
        row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE5F4EE" } };
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
}

export async function generateSalarySlipPdf({ payrollItem, employee, store, companyName }) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  const left = 18;
  let y = 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(companyName || "Company", left, y);
  y += 8;
  doc.setFontSize(12);
  doc.text(`Salary Slip - ${formatMonth(payrollItem.month)}`, left, y);
  y += 10;

  doc.setFont("helvetica", "normal");
  const rows = [
    ["Employee", `${employee.employeeCode} - ${employee.fullName}`],
    ["Store", store?.name || ""],
    ["Gross salary", currency(payrollItem.grossSalary)],
    ["Present days", payrollItem.attendanceSummary.presentDays],
    ["Half days", payrollItem.attendanceSummary.halfDays],
    ["Absences / offs taken", payrollItem.attendanceSummary.totalOffsTaken],
    ["Leave deduction days", payrollItem.attendanceSummary.leaveDeductionDays],
    ["Leave deduction amount", currency(payrollItem.attendanceSummary.leaveDeductionAmount)],
    ["Advance / loan deduction", currency(payrollItem.advanceDeduction)],
    ["Reimbursements", currency(payrollItem.reimbursementAmount)],
    ["Staff welfare", currency(payrollItem.staffWelfare)],
    ["Incentive payable", currency(payrollItem.incentivePayable)],
    ["Net payable", currency(payrollItem.netPayable)]
  ];

  for (const [label, value] of rows) {
    doc.setFont("helvetica", label === "Net payable" ? "bold" : "normal");
    doc.text(String(label), left, y);
    doc.text(String(value), 110, y);
    y += 8;
  }

  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Prepared date: ${new Date().toLocaleDateString("en-IN")}`, left, y);
  y += 6;
  doc.text("Notes: Payroll is generated from attendance punches, approved deductions and approved additions.", left, y);

  return doc.output("blob");
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
